
import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { EmailLog, UserProfile, Contact } from '../../types';
import { Mail, CheckCircle2, XCircle, Clock, Building2, AlertTriangle, Info, Eye, X, User, RefreshCw, Loader2 } from 'lucide-react';
import { sendReportEmail, logEmailAttempt, listProjectPdfs, getPdfPublicUrl, getReportDateFromHistory } from '../../services/cloudService';
import { markRepeatSubject, stripRepeatPrefix } from './projectHubLogic';
import { newSendKey } from '../../services/pendingSendPersistence';
import { readOnlyMessage, readOnlyTitle } from '../../services/readOnlyNotice';

interface Props {
    logs: EmailLog[];
    users?: UserProfile[];
    contacts?: Contact[];
    projectId?: string;
    projectName?: string;
    projectNumber?: string;
    currentUser?: UserProfile | null;
    onResendSuccess?: () => void;
    /**
     * Schreibgeschuetzt geoeffnetes Projekt: der Sendeverlauf darf gelesen
     * werden, der Wiederholungs-Versand nicht — er geht an echte Empfaenger
     * und legt zusaetzlich eine Zeile im Sendeprotokoll an.
     */
    isReadOnly?: boolean;
    /** Laden gescheitert: dann nicht „Kein Sendeverlauf“ behaupten. */
    loadFailed?: boolean;
}

export const ProjectSentReportsView: React.FC<Props> = ({
    logs,
    users = [],
    contacts = [],
    projectId,
    projectName,
    projectNumber,
    currentUser,
    onResendSuccess,
    isReadOnly = false,
    loadFailed = false
}) => {
    const [viewLog, setViewLog] = useState<EmailLog | null>(null);
    const [resendLog, setResendLog] = useState<EmailLog | null>(null);
    const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(new Set());
    const [isSending, setIsSending] = useState(false);
    const [sendSuccess, setSendSuccess] = useState(false);
    
    // Archive Selection State
    const [archivePdfs, setArchivePdfs] = useState<any[]>([]);
    const [selectedArchivePdf, setSelectedArchivePdf] = useState<string | null>(null);
    const [isSearchingArchive, setIsSearchingArchive] = useState(false);
    const [showArchiveSelector, setShowArchiveSelector] = useState(false);
    const [archiveSearchTerm, setArchiveSearchTerm] = useState('');
    const [activeSearchTerms, setActiveSearchTerms] = useState<string[]>([]);

    const loadArchivePdfs = async () => {
        if (!resendLog) return;
        setIsSearchingArchive(true);
        try {
            const terms = [];
            
            // 1. Primary search term: Project Number (most specific for server-side list)
            if (projectNumber) terms.push(projectNumber);
            else if (projectName) terms.push(projectName);

            // 2. Secondary search term: Date (for client-side filtering)
            const match = resendLog.subject.match(/Nr\.\s*(\d+)/);
            const inspectionNumber = match ? parseInt(match[1]) : null;
            
            let reportDate = null;
            if (projectId && inspectionNumber) {
                try {
                    reportDate = await getReportDateFromHistory(projectId, inspectionNumber);
                } catch (e) {
                    console.warn("Could not fetch report date from history", e);
                }
            }
            
            if (!reportDate) {
                reportDate = resendLog.sent_at.split('T')[0];
            }
            
            if (reportDate) {
                terms.push(reportDate.replace(/-/g, '_'));
            }

            // Add project name if not already used as primary
            if (projectName && projectNumber) terms.push(projectName);
            
            setActiveSearchTerms(terms);
            const files = await listProjectPdfs(terms);
            setArchivePdfs(files);
            setShowArchiveSelector(true);
        } catch (error) {
            console.error("Failed to load archive PDFs", error);
            alert("Fehler beim Laden des Archivs.");
        } finally {
            setIsSearchingArchive(false);
        }
    };

    const filteredArchivePdfs = archivePdfs.filter(file => 
        file.name.toLowerCase().includes(archiveSearchTerm.toLowerCase())
    );

    const formatDate = (d: string) => new Date(d).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });

    const getSenderName = (senderId: string) => {
        const user = users.find(u => u.id === senderId);
        if (user) return `${user.first_name} ${user.last_name}`;
        return 'System / Unbekannt';
    };

    const handleStartResend = (log: EmailLog) => {
        setResendLog(log);
        setSendSuccess(false);
        setSelectedArchivePdf(null);
        setShowArchiveSelector(false);
        setArchiveSearchTerm('');
        // Pre-select original recipients if they are in the contact list
        const initial = new Set<string>();
        const originalEmails = log.recipients.split(',').map(e => e.trim());
        contacts.forEach(c => {
            if (originalEmails.includes(c.email)) {
                initial.add(c.email);
            }
        });
        setSelectedRecipients(initial);
    };

    const toggleRecipient = (email: string) => {
        const newSet = new Set(selectedRecipients);
        if (newSet.has(email)) newSet.delete(email); else newSet.add(email);
        setSelectedRecipients(newSet);
    };

    const handleResend = async () => {
        if (!resendLog || selectedRecipients.size === 0) return;
        // Zweite Reihe hinter dem deaktivierten Knopf: der Versand laeuft ueber
        // cloudService direkt raus, an App.handleUpdateReport vorbei. Eine
        // versehentlich erneut zugestellte Mail laesst sich nicht zuruecknehmen.
        if (isReadOnly) {
            alert(readOnlyMessage('Es wurde keine E-Mail versendet und nichts protokolliert.'));
            return;
        }
        setIsSending(true);
        // Eigener Schlüssel statt des Schlüssels aus resendLog: Die Wiederholung
        // ist ein NEUER Sendevorgang mit einer echten zweiten Mail und gehört
        // sichtbar in den Sendeverlauf. Würde hier der Ursprungsschlüssel
        // wiederverwendet, verwürfe der Unique-Index den Eintrag und die
        // Wiederholung wäre nirgends belegt.
        const sendKey = newSendKey();
        try {
            // 1:1 Übernahme des Body (inkl. Signatur) ohne zusätzlichen Hinweis, wie vom User gewünscht
            const bodyHtml = resendLog.body_html;
            
            const senderName = currentUser 
                ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
                : getSenderName(resendLog.sender_id);
            
            const replyTo = currentUser?.email;

            const pdfUrl = resendLog.pdf_url || (selectedArchivePdf ? getPdfPublicUrl(selectedArchivePdf) : undefined);
            const filename = pdfUrl?.split('/').pop() || 'bericht.pdf';

            await sendReportEmail({
                recipients: Array.from(selectedRecipients),
                // Kunden-Mail ohne interne Wiederholungs-Markierung (auch aus Alt-Logs)
                subject: stripRepeatPrefix(resendLog.subject),
                html: bodyHtml,
                pdfUrl: pdfUrl,
                filename: filename,
                senderName,
                replyTo
            });

            try {
                await logEmailAttempt({
                    project_id: projectId || resendLog.project_id,
                    project_name: projectName || resendLog.project_name,
                    recipients: Array.from(selectedRecipients).join(', '),
                    subject: markRepeatSubject(resendLog.subject),
                    status: 'success',
                    body_html: bodyHtml,
                    pdf_url: pdfUrl,
                    send_key: sendKey
                });
            } catch (logError) {
                // Die Mail ist bereits raus — ein fehlgeschlagener Logeintrag darf
                // die Wiederholung nicht als Fehler erscheinen lassen.
                console.error('Sendebericht (Wiederholung) konnte nicht gespeichert werden:', logError);
            }

            setSendSuccess(true);
            
            // Trigger refresh in parent
            if (onResendSuccess) {
                onResendSuccess();
            }

            setTimeout(() => {
                setResendLog(null);
                setSendSuccess(false);
            }, 2000);
        } catch (error) {
            console.error("Resend failed", error);
            alert("Fehler beim erneuten Senden.");
        } finally {
            setIsSending(false);
        }
    };

    if (loadFailed) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-red-500 bg-slate-50 h-full text-center px-6">
                <AlertTriangle className="w-16 h-16 opacity-40 mb-4" />
                <p className="font-bold">Sendeverlauf konnte nicht geladen werden</p>
                <p className="text-xs text-slate-500">Die Sendeberichte sind nicht verloren. Bitte Verbindung prüfen und den Sendeverlauf erneut öffnen.</p>
            </div>
        );
    }

    if (logs.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400 bg-slate-50 h-full">
                <Mail className="w-16 h-16 opacity-10 mb-4" />
                <p className="font-bold">Kein Sendeverlauf vorhanden</p>
                <p className="text-xs">Berichte wurden bisher nur lokal (Outlook) oder noch gar nicht versendet.</p>
            </div>
        );
    }

    return (
        <div data-tutorial="versand.sendeverlauf" className="p-6 bg-slate-50 h-full overflow-y-auto space-y-6 relative">
            
            {/* RESEND MODAL */}
            {resendLog && (
                <div className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in flex flex-col relative border border-slate-200">
                        <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                            <h3 className="font-bold text-lg flex items-center gap-2"><RefreshCw className="w-5 h-5 text-blue-400" /> Bericht erneut senden</h3>
                            <button onClick={() => setResendLog(null)} className="hover:bg-white/10 p-1 rounded transition-colors"><X className="w-5 h-5"/></button>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            {sendSuccess ? (
                                <div className="flex flex-col items-center justify-center py-8 text-center animate-scale-in">
                                    <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4">
                                        <CheckCircle2 className="w-8 h-8" />
                                    </div>
                                    <h4 className="font-bold text-slate-800">Erfolgreich versendet!</h4>
                                    <p className="text-xs text-slate-500 mt-1">Der Bericht wurde an die ausgewählten Empfänger gesendet.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="bg-blue-50 border border-blue-100 p-3 rounded-xl flex items-start gap-2">
                                        <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                                        <p className="text-[11px] text-blue-700 leading-relaxed">
                                            Wählen Sie die Empfänger aus, an die der Bericht erneut gesendet werden soll. 
                                            Der Inhalt wird 1:1 übernommen.
                                        </p>
                                    </div>

                                    {!resendLog.pdf_url && (
                                        <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl space-y-3">
                                            <div className="flex items-start gap-2">
                                                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                                <div className="flex-1">
                                                    <p className="text-[11px] font-bold text-amber-800">Kein PDF-Link gefunden</p>
                                                    <p className="text-[11px] text-amber-700 leading-relaxed">
                                                        Dieser Bericht wurde vor der Archivierungs-Funktion gesendet. 
                                                        Sie können das PDF manuell aus dem Archiv auswählen.
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            {selectedArchivePdf ? (
                                                <div className="flex items-center justify-between bg-white p-2 rounded-lg border border-amber-200">
                                                    <span className="text-[11px] font-mono truncate max-w-[200px]">{selectedArchivePdf}</span>
                                                    <button 
                                                        onClick={() => setSelectedArchivePdf(null)}
                                                        className="text-[11px] text-red-500 font-bold hover:underline"
                                                    >
                                                        Ändern
                                                    </button>
                                                </div>
                                            ) : (
                                                <button 
                                                    onClick={loadArchivePdfs}
                                                    disabled={isSearchingArchive}
                                                    className="w-full py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 text-[11px] font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                                                >
                                                    {isSearchingArchive ? <Loader2 className="w-3 h-3 animate-spin" /> : <Building2 className="w-3 h-3" />}
                                                    PDF manuell aus Archiv wählen
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <div>
                                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-2">Empfänger auswählen</label>
                                        <div className="border border-slate-200 rounded-xl max-h-60 overflow-y-auto">
                                            {contacts.filter(c => c.email).map(c => (
                                                <div 
                                                    key={c.id} 
                                                    onClick={() => toggleRecipient(c.email)}
                                                    className={`flex items-center gap-3 p-3 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 transition-colors ${selectedRecipients.has(c.email) ? 'bg-blue-50/50' : ''}`}
                                                >
                                                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedRecipients.has(c.email) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'}`}>
                                                        {selectedRecipients.has(c.email) && <CheckCircle2 className="w-2.5 h-2.5" />}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="text-xs font-bold text-slate-700">{c.name}</div>
                                                        <div className="text-[11px] text-slate-400">{c.email}</div>
                                                    </div>
                                                </div>
                                            ))}
                                            {contacts.filter(c => c.email).length === 0 && (
                                                <div className="p-4 text-center text-xs text-slate-400 italic">Keine Kontakte mit E-Mail gefunden.</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="pt-4 flex gap-3">
                                        <button 
                                            onClick={() => setResendLog(null)}
                                            className="flex-1 px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                                        >
                                            Abbrechen
                                        </button>
                                        <button
                                            onClick={handleResend}
                                            disabled={isSending || isReadOnly || selectedRecipients.size === 0}
                                            title={isReadOnly ? readOnlyTitle('Der Bericht wird nicht erneut versendet.') : undefined}
                                            className="flex-1 px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {isSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                            Jetzt erneut senden
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* EMAIL CONTENT MODAL */}
            {viewLog && (
                <div className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden animate-scale-in relative border border-slate-200">
                        <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                            <div>
                                <h3 className="font-bold text-lg flex items-center gap-2">Gesendete Nachricht</h3>
                                <div className="text-xs text-slate-400 mt-1 flex flex-col gap-0.5">
                                    <span>An: {viewLog.recipients}</span>
                                    <span>Betreff: {viewLog.subject}</span>
                                </div>
                            </div>
                            <button onClick={() => setViewLog(null)} className="hover:bg-white/10 p-1 rounded transition-colors"><X className="w-6 h-6"/></button>
                        </div>
                        <div className="flex-1 bg-white p-8 overflow-y-auto">
                            {viewLog.body_html ? (
                                <div className="prose prose-sm max-w-none">
                                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewLog.body_html) }} />
                                </div>
                            ) : (
                                <div className="text-center text-slate-400 py-10 italic">
                                    Inhalt nicht verfügbar (Alt-Daten).
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ARCHIVE SELECTOR MODAL */}
            {showArchiveSelector && (
                <div className="fixed inset-0 z-[400] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in flex flex-col relative border border-slate-200">
                        <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                            <h3 className="font-bold text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-amber-400" /> Archiv-Auswahl</h3>
                            <button onClick={() => setShowArchiveSelector(false)} className="hover:bg-white/10 p-1 rounded transition-colors"><X className="w-5 h-5"/></button>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-2">
                                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Aktive Suchfilter (Cloud-Archiv):</p>
                                <div className="flex flex-wrap gap-1">
                                    {activeSearchTerms.map((term, i) => (
                                        <span key={i} className={`px-2 py-0.5 rounded-md text-[11px] font-mono ${i === 0 ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-slate-200 text-slate-600'}`}>
                                            {term} {i === 0 && '(Server)'}
                                        </span>
                                    ))}
                                </div>
                                <p className="text-[10px] text-slate-400 italic">
                                    Es wird im Ordner <code className="bg-slate-200 px-1 rounded">reports/pdfs</code> gesucht.
                                </p>
                            </div>

                            <p className="text-xs text-slate-500">
                                Folgende PDFs wurden gefunden. Bitte wählen Sie das passende aus:
                            </p>

                            <div className="relative">
                                <input 
                                    type="text"
                                    placeholder="Nach Dateiname suchen..."
                                    value={archiveSearchTerm}
                                    onChange={(e) => setArchiveSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 bg-slate-100 border-none rounded-xl text-xs focus:ring-2 focus:ring-blue-500"
                                />
                                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            </div>
                            
                            <div className="border border-slate-200 rounded-xl max-h-80 overflow-y-auto">
                                {filteredArchivePdfs.map(file => (
                                    <div 
                                        key={file.name}
                                        onClick={() => {
                                            setSelectedArchivePdf(file.name);
                                            setShowArchiveSelector(false);
                                        }}
                                        className="p-3 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-blue-50 transition-colors group"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[11px] font-mono text-slate-700 truncate">{file.name}</div>
                                                <div className="text-[10px] text-slate-400 mt-0.5">
                                                    {new Date(file.created_at).toLocaleDateString()} • {(file.metadata.size / 1024 / 1024).toFixed(2)} MB
                                                </div>
                                            </div>
                                            <CheckCircle2 className="w-4 h-4 text-blue-500 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity" />
                                        </div>
                                    </div>
                                ))}
                                {filteredArchivePdfs.length === 0 && (
                                    <div className="p-10 text-center space-y-2">
                                        <AlertTriangle className="w-8 h-8 text-slate-300 mx-auto" />
                                        <p className="text-xs text-slate-400 italic">Keine passenden PDFs im Archiv gefunden.</p>
                                    </div>
                                )}
                            </div>
                            
                            <button 
                                onClick={() => setShowArchiveSelector(false)}
                                className="w-full py-3 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition-colors"
                            >
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-4">
                {logs.map((log) => (
                    <div key={log.id} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:border-blue-300 transition-all">
                        <div className="flex flex-col md:flex-row justify-between gap-4">
                            <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-2">
                                    {log.status === 'success' ? (
                                        <span className="flex items-center gap-1 text-emerald-600 font-bold text-[11px] bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                                            <CheckCircle2 className="w-3 h-3" /> Zustellung erfolgreich
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1 text-red-600 font-bold text-[11px] bg-red-50 px-2 py-0.5 rounded-full border border-red-100">
                                            <XCircle className="w-3 h-3" /> Sende-Fehler
                                        </span>
                                    )}
                                    {log.subject.includes('[Wiederholung]') && (
                                        <span className="flex items-center gap-1 text-blue-600 font-bold text-[11px] bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">
                                            <RefreshCw className="w-3 h-3" /> Erneut gesendet
                                        </span>
                                    )}
                                    <span className="text-[11px] text-slate-400 font-medium flex items-center gap-1">
                                        <Clock className="w-3 h-3" /> {formatDate(log.sent_at)}
                                    </span>
                                </div>
                                <h4 className="font-bold text-slate-800 leading-tight">Betreff: {log.subject}</h4>
                                <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                                    <User className="w-3 h-3"/> Absender: {getSenderName(log.sender_id)}
                                </p>
                            </div>
                            
                            <div className="flex flex-col items-end gap-2">
                                {log.status === 'error' && log.error_message && (
                                    <div className="md:w-64 bg-red-50 p-3 rounded-xl border border-red-100 flex items-start gap-2 animate-pulse mb-2">
                                        <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                        <div className="text-[11px] text-red-700 leading-relaxed font-mono">
                                            {log.error_message}
                                        </div>
                                    </div>
                                )}
                                
                                <div className="flex gap-2">
                                    {log.pdf_url && (
                                        <button
                                            onClick={() => handleStartResend(log)}
                                            disabled={isReadOnly}
                                            title={isReadOnly ? readOnlyTitle('Der Bericht wird nicht erneut versendet.') : undefined}
                                            className="flex items-center gap-2 text-xs font-bold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-lg transition-colors shadow-sm border border-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-50"
                                        >
                                            <RefreshCw className="w-4 h-4" /> Erneut senden
                                        </button>
                                    )}
                                    <button 
                                        onClick={() => setViewLog(log)}
                                        className="flex items-center gap-2 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors shadow-sm border border-blue-100"
                                    >
                                        <Eye className="w-4 h-4" /> Inhalt anzeigen
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-[11px] text-blue-700 leading-relaxed">
                    Hier werden nur Berichte gelistet, die über die <b>Cloud-Sende-Funktion</b> direkt aus der App verschickt wurden. 
                    Lokale Exporte oder "mailto"-Versand werden nicht protokolliert.
                </p>
            </div>
        </div>
    );
};
